import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import type {
  MatchStageParam,
  MatchStageResponseKey,
  MatchStatus,
  MatchesByStage,
  RawMatchesByStage,
  TeamInfo,
  WorldCupMatch,
} from './types';

export const STAGE_RESPONSE_ORDER: Array<{
  value: MatchStageParam;
  responseKey: MatchStageResponseKey;
  i18nKey: string;
}> = [
  { value: 'group', responseKey: 'group', i18nKey: 'stage.group' },
  { value: 'round_of_32', responseKey: 'roundOf32', i18nKey: 'stage.roundOf32' },
  { value: 'round_of_16', responseKey: 'roundOf16', i18nKey: 'stage.roundOf16' },
  { value: 'quarter_final', responseKey: 'quarterFinal', i18nKey: 'stage.quarterFinal' },
  { value: 'semi_final', responseKey: 'semiFinal', i18nKey: 'stage.semiFinal' },
  { value: 'third_place', responseKey: 'thirdPlace', i18nKey: 'stage.thirdPlace' },
  { value: 'final', responseKey: 'final', i18nKey: 'stage.final' },
];

export function normalizeMatchesByStage(raw?: RawMatchesByStage | null): MatchesByStage {
  return {
    group: ensureArray(raw?.group),
    roundOf32: ensureArray(raw?.roundOf32 ?? raw?.round_of_32),
    roundOf16: ensureArray(raw?.roundOf16 ?? raw?.round_of_16),
    quarterFinal: ensureArray(raw?.quarterFinal ?? raw?.quarter_final),
    semiFinal: ensureArray(raw?.semiFinal ?? raw?.semi_final),
    thirdPlace: ensureArray(raw?.thirdPlace ?? raw?.third_place),
    final: ensureArray(raw?.final),
  };
}

function ensureArray(value: unknown): WorldCupMatch[] {
  return Array.isArray(value) ? value : [];
}

function toNumber(value?: number | string | null) {
  const num = Number(value ?? 0);

  return Number.isFinite(num) ? num : 0;
}

export function formatInt(value?: number | string | null) {
  return new Intl.NumberFormat('en-US').format(toNumber(value));
}

export function formatShortTime(value?: string | null) {
  if (!value) return '--';

  return dayjs(value).format('MM-DD HH:mm');
}

export function formatTime(value?: string | null) {
  if (!value) return '--';

  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

export function formatRelativeTime(value?: string | null) {
  if (!value) return '--';
  const date = dayjs(value);
  return date.isValid() ? date.fromNow() : '--';
}

export function getTeamName(team?: TeamInfo | null, locale?: string) {
  if (!team) return '';
  if (locale === 'zh' || locale === 'cn') {
    return team.nameZh || team.nameEn || team.code || '';
  }
  return team.nameEn || team.nameZh || team.code || '';
}

export function getMatchScore(match: WorldCupMatch) {
  if (match.status !== 'live' && match.status !== 'finished') {
    return '-';
  }

  const homeScore = match.homeScore ?? '-';
  const awayScore = match.awayScore ?? '-';
  const normalScore = `${homeScore} - ${awayScore}`;

  if (!match.wentToPenalties) {
    return normalScore;
  }

  return `${normalScore} (${match.homePenalties ?? '-'} - ${match.awayPenalties ?? '-'})`;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function getMatchTimeText(match: WorldCupMatch, t?: Translate) {
  if (match.status === 'live') {
    if (!t) {
      return `Live ${match.liveMinute ? ` ${match.liveMinute}'` : ''}`;
    }
    return t('module.worldCup.focusMatch.match.liveMinute', { minute: match.liveMinute ?? '--' })
  }

  if (match.status === 'finished') {
    return 'finished';
  }

  return formatTime(match.kickoffAt);
}

export function getMatchStatusKey(status?: MatchStatus) {
  return status || 'unknown';
}

export function getStatusColor(status?: MatchStatus) {
  const map: Record<MatchStatus, string> = {
    scheduled: '#60a5fa',
    live: '#22c55e',
    finished: '#8b8d98',
    cancelled: '#ef4444',
    postponed: '#f59e0b',
  };

  return status ? map[status] : '#8b8d98';
}
